import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { faClass } from '@/constants/icons';
import i18n from '@/i18n';
import { fetchUserByFilter } from '@/store/actions/UsersActions';

import styles from './GlobalSearch.module.scss';
import { Icon } from '@ui';

function useDebounced(value, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

// Soglia minima per la ricerca utenti via rete (coerente col CustomerPicker).
const USER_FETCH_MIN = 3;
// Campi su cui ricomporre la stringa cercabile dell'utente (match multi-token).
const USER_FIELDS = ['nome', 'cognome', 'email', 'telefono'];

// Il backend filtra su singolo campo: "Mario Rossi" non matcha perché nessun
// campo contiene l'intera stringa. Interroghiamo col token più selettivo e
// filtriamo lato client richiedendo che TUTTI i token compaiano nell'utente.
function userMatchesTokens(u, tokens) {
  const hay = USER_FIELDS.map((f) => String(u?.[f] ?? ''))
    .join(' ')
    .toLowerCase();
  return tokens.every((t) => hay.includes(t));
}

/**
 * Ricerca globale cross-entità nel topbar.
 * Interroga i dataset già presenti in Redux (autisti, mezzi, aziende, utenti)
 * — nessuna chiamata di rete — e naviga al dettaglio selezionato.
 */

const MAX_PER_GROUP = 6;

// Configurazione delle entità ricercabili. `fields` = campi su cui matchare;
// `label`/`sub` costruiscono le due righe del risultato; `to` produce la rotta.
const ENTITIES = [
  {
    key: 'drivers',
    title: i18n.t('autisti', { defaultValue: 'Autisti' }),
    icon: 'fa-id-card',
    fields: ['nome', 'cognome', 'email', 'telefono', 'numero_patente', 'mezzo', 'targa'],
    label: (r) => `${r.nome || ''} ${r.cognome || ''}`.trim() || r.id,
    sub: (r) => r.mezzo || r.telefono || r.email || '',
    to: (r) => ({ path: `/fleet/drivers/${r.id}`, options: { state: { row: r } } }),
  },
  {
    key: 'vehicles',
    title: i18n.t('mezzi', { defaultValue: 'Mezzi' }),
    icon: 'fa-van-shuttle',
    fields: ['label', 'targa', 'model', 'type'],
    label: (r) => r.targa || r.label || r.id,
    sub: (r) => [r.model, r.type].filter(Boolean).join(' · '),
    to: (r) => ({ path: `/fleet/vehicles/${r.id}`, options: { state: { row: r } } }),
  },
  {
    key: 'companies',
    title: i18n.t('companies', { defaultValue: 'Aziende' }),
    icon: 'fa-building',
    fields: ['label', 'code', 'email', 'address'],
    label: (r) => r.label || r.code || r.id,
    sub: (r) => r.code || r.email || '',
    to: (r) => ({ path: `/companies/${r.code}`, options: {} }),
  },
  {
    key: 'users',
    title: i18n.t('utenti', { defaultValue: 'Utenti' }),
    icon: 'fa-user',
    fields: ['nome', 'cognome', 'email', 'telefono'],
    label: (r) => `${r.nome || ''} ${r.cognome || ''}`.trim() || r.id,
    sub: (r) => r.telefono || r.email || '',
    to: (r) => ({ path: `/users/${r.id}`, options: { state: { row: r } } }),
  },
];

function matches(row, fields, term) {
  for (const f of fields) {
    const v = row[f];
    if (v && String(v).toLowerCase().includes(term)) return true;
  }
  return false;
}

export default function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Utenti recuperati via rete: in topbar la lista Redux è popolata solo nella
  // pagina "Utenti", quindi qui interroghiamo sempre il backend (come CustomerPicker).
  const debounced = useDebounced(query, 350);
  const [remoteUsers, setRemoteUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const usersCacheRef = useRef(new Map());
  const usersAbortRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const term = debounced.trim();
    if (term.length < USER_FETCH_MIN) {
      setRemoteUsers([]);
      setUsersLoading(false);
      return undefined;
    }

    const cache = usersCacheRef.current;
    if (cache.has(term)) {
      setRemoteUsers(cache.get(term));
      return undefined;
    }

    if (usersAbortRef.current) usersAbortRef.current.abort();
    const controller = new AbortController();
    usersAbortRef.current = controller;

    // Token più lungo = più selettivo: lo usiamo come query al backend.
    const tokens = term.toLowerCase().split(/\s+/).filter(Boolean);
    const queryToken = tokens.reduce((a, b) => (b.length > a.length ? b : a), '');

    setUsersLoading(true);
    (async () => {
      try {
        const items = await dispatch(fetchUserByFilter(queryToken, { limitN: 20 }));
        if (!alive) return;
        const list = Array.isArray(items) ? items : [];
        // Con più token, filtriamo lato client per AND su tutti i token.
        const filtered =
          tokens.length > 1 ? list.filter((u) => userMatchesTokens(u, tokens)) : list;
        const result = filtered.slice(0, MAX_PER_GROUP);
        cache.set(term, result);
        setRemoteUsers(result);
      } catch (e) {
        if (e?.name !== 'AbortError' && alive) setRemoteUsers([]);
      } finally {
        if (alive) setUsersLoading(false);
      }
    })();

    return () => {
      alive = false;
      if (usersAbortRef.current) usersAbortRef.current.abort();
    };
  }, [debounced, dispatch]);

  const datasets = useSelector(
    (state) => ({
      drivers: state.drivers || [],
      vehicles: state.vehicles || [],
      companies: state.companies || [],
      users: state?.users?.list || [],
    }),
    shallowEqual
  );

  // Gruppi di risultati + lista piatta (per la navigazione da tastiera).
  const { groups, flat } = useMemo(() => {
    const term = query.toLowerCase().trim();
    if (term.length < 2) return { groups: [], flat: [] };

    const groups = [];
    const flat = [];
    for (const ent of ENTITIES) {
      let hits = [];
      if (ent.key === 'users') {
        // Utenti: usiamo i risultati recuperati dal backend (già filtrati).
        hits = remoteUsers.slice(0, MAX_PER_GROUP);
      } else {
        const rows = datasets[ent.key] || [];
        for (const r of rows) {
          if (matches(r, ent.fields, term)) {
            hits.push(r);
            if (hits.length >= MAX_PER_GROUP) break;
          }
        }
      }
      if (hits.length) {
        const items = hits.map((r) => ({ ent, row: r, flatIndex: flat.length + 0 }));
        // assegna l'indice piatto progressivo
        items.forEach((it) => {
          it.flatIndex = flat.length;
          flat.push(it);
        });
        groups.push({ ent, items });
      }
    }
    return { groups, flat };
  }, [query, datasets, remoteUsers]);

  // Chiusura su click esterno
  useEffect(() => {
    function onDocMouseDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  const select = useCallback(
    (item) => {
      if (!item) return;
      const { path, options } = item.ent.to(item.row);
      setOpen(false);
      setQuery('');
      navigate(path, options);
    },
    [navigate]
  );

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!open || flat.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % flat.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + flat.length) % flat.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      select(flat[activeIndex]);
    }
  };

  const hasQuery = query.trim().length >= 2;
  const showPopover = open && hasQuery;

  return (
    <div className={styles.root} ref={rootRef}>
      <div className={styles.inputWrap}>
        <Icon name="search" aria-hidden="true" />
        <input
          type="text"
          role="combobox"
          aria-expanded={showPopover}
          aria-controls="global-search-results"
          placeholder={i18n.t('cerca_globale', {
            defaultValue: 'Cerca autisti, mezzi, aziende, utenti…',
          })}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIndex(0);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
      </div>

      {showPopover && (
        <div className={styles.popover} id="global-search-results" role="listbox">
          {flat.length === 0 ? (
            <div className={styles.empty}>
              {usersLoading ? (
                <>
                  <Icon name="loading" />
                  <span>{i18n.t('ricerca_in_corso', { defaultValue: 'Ricerca in corso…' })}</span>
                </>
              ) : (
                <>
                  <Icon name="search" />
                  <span>{i18n.t('nessun_risultato', { defaultValue: 'Nessun risultato' })}</span>
                </>
              )}
            </div>
          ) : (
            groups.map((g) => (
              <div key={g.ent.key} className={styles.group}>
                <div className={styles.groupTitle}>
                  <i className={faClass(g.ent.icon)}></i>
                  {g.ent.title}
                </div>
                {g.items.map((it) => (
                  <button
                    type="button"
                    key={`${g.ent.key}-${it.row.id || it.flatIndex}`}
                    role="option"
                    aria-selected={it.flatIndex === activeIndex}
                    className={`${styles.item} ${it.flatIndex === activeIndex ? styles.active : ''}`}
                    onMouseEnter={() => setActiveIndex(it.flatIndex)}
                    onClick={() => select(it)}
                  >
                    <span className={styles.itemLabel}>{g.ent.label(it.row)}</span>
                    {g.ent.sub(it.row) ? (
                      <span className={styles.itemSub}>{g.ent.sub(it.row)}</span>
                    ) : null}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
